import {FC, Fragment, useEffect, useMemo, useState} from 'react'
import Swal from 'sweetalert2'
import {ID, KTIcon} from '../../../_metronic/helpers'
import {MachineFranchise, MachineFranchiseUnit} from '../core/_models'
import {getMachineFranchises, syncMachineFranchises} from '../core/_request'

type Props = {
  machineId: ID
}

type LinkFilter = 'all' | 'linked' | 'unlinked'

const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

const unitLabel = (unit: MachineFranchiseUnit) => (unit.id ? `Unidade #${unit.id}` : 'Nova unidade')

export const MachineFranchises: FC<Props> = ({machineId}) => {
  const [franchises, setFranchises] = useState<MachineFranchise[]>([])
  const [original, setOriginal] = useState<MachineFranchise[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [linkFilter, setLinkFilter] = useState<LinkFilter>('all')

  const load = async () => {
    setLoading(true)
    try {
      const data = await getMachineFranchises(machineId)
      setFranchises(data)
      setOriginal(data)
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error?.response?.data?.message ?? 'Erro ao carregar as franquias da máquina',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineId])

  const visible = useMemo(() => {
    const term = normalize(search)
    return franchises.filter(
      (franchise) =>
        normalize(franchise.name).includes(term) &&
        (linkFilter === 'all' || (linkFilter === 'linked') === franchise.units.length > 0)
    )
  }, [franchises, search, linkFilter])

  const linkedCount = franchises.filter((franchise) => franchise.units.length > 0).length
  const unitsCount = franchises.reduce((total, franchise) => total + franchise.units.length, 0)

  const setUnits = (franchiseId: number, units: MachineFranchiseUnit[]) =>
    setFranchises((current) =>
      current.map((franchise) => (franchise.id === franchiseId ? {...franchise, units} : franchise))
    )

  const addUnit = (franchise: MachineFranchise) =>
    setUnits(franchise.id, [...franchise.units, {id: null, rooms: []}])

  const removeUnit = (franchise: MachineFranchise, index: number) =>
    setUnits(
      franchise.id,
      franchise.units.filter((_, i) => i !== index)
    )

  const toggleRoom = (franchise: MachineFranchise, index: number, roomId: number) =>
    setUnits(
      franchise.id,
      franchise.units.map((unit, i) =>
        i !== index
          ? unit
          : {
              ...unit,
              rooms: unit.rooms.includes(roomId)
                ? unit.rooms.filter((id) => id !== roomId)
                : [...unit.rooms, roomId],
            }
      )
    )

  // Uma sala não pode ter duas unidades da mesma máquina
  const roomOwner = (franchise: MachineFranchise, roomId: number) =>
    franchise.units.findIndex((unit) => unit.rooms.includes(roomId))

  const save = async () => {
    const currentIds = new Set(
      franchises.flatMap((franchise) => franchise.units.map((unit) => unit.id)).filter(Boolean)
    )
    const removed = original.flatMap((franchise) =>
      franchise.units
        .filter((unit) => !currentIds.has(unit.id))
        .map((unit) => ({franchise: franchise.name, unit}))
    )
    const removedWithRooms = removed.filter(({unit}) => unit.rooms.length > 0)
    const created = franchises.reduce(
      (total, franchise) => total + franchise.units.filter((unit) => !unit.id).length,
      0
    )
    const withoutRooms = franchises.reduce(
      (total, franchise) => total + franchise.units.filter((unit) => !unit.rooms.length).length,
      0
    )

    const result = await Swal.fire({
      title: 'Deseja salvar as franquias da máquina?',
      html:
        `${unitsCount} unidade(s) em ${linkedCount} franquia(s).` +
        (created ? `<br/>${created} unidade(s) nova(s).` : '') +
        (removed.length ? `<br/>${removed.length} unidade(s) removida(s).` : '') +
        (removedWithRooms.length
          ? `<br/><br/><b>Unidades removidas que estavam em salas:</b><br/>` +
            removedWithRooms
              .map(({franchise, unit}) => `${franchise}: ${unitLabel(unit)}`)
              .join('<br/>')
          : '') +
        (withoutRooms
          ? `<br/><br/><b>${withoutRooms} unidade(s) sem sala</b>: não poderão ser usadas na agenda.`
          : ''),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-secondary',
      },
    })
    if (!result.isConfirmed) return

    setSaving(true)
    try {
      await syncMachineFranchises(machineId, {
        franchises: franchises
          .filter((franchise) => franchise.units.length > 0)
          .map((franchise) => ({id: franchise.id, units: franchise.units})),
      })
      await Swal.fire({
        position: 'center',
        icon: 'success',
        title: 'Salvo com sucesso',
        showConfirmButton: false,
        timer: 1500,
      })
      await load()
    } catch (error: any) {
      let text = error?.response?.data?.message
      if (error?.response?.status === 422) {
        text = Object.values(error.response.data.errors ?? {})
          .map((messages: any) => messages[0])
          .join('<br/>')
      }
      await Swal.fire({icon: 'error', title: 'Oops...', html: text})
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title flex-column'>
          <h3 className='fw-bold mb-1'>Franquias e salas</h3>
          <span className='text-muted fs-7'>
            {unitsCount} unidade(s) em {linkedCount} de {franchises.length} franquias. Cada unidade é
            uma máquina física; só aparecem as salas com Depilação a Laser.
          </span>
        </div>
        <div className='card-toolbar gap-3'>
          <div className='position-relative'>
            <KTIcon iconName='magnifier' className='fs-3 position-absolute ms-4 top-50 translate-middle-y' />
            <input
              type='text'
              className='form-control form-control-solid w-225px ps-12'
              placeholder='Pesquisar franquia'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className='form-select form-select-solid w-175px'
            value={linkFilter}
            onChange={(e) => setLinkFilter(e.target.value as LinkFilter)}
          >
            <option value='all'>Todas</option>
            <option value='linked'>Com unidades</option>
            <option value='unlinked'>Sem unidades</option>
          </select>
        </div>
      </div>

      <div className='card-body py-4'>
        {loading ? (
          <div className='d-flex justify-content-center py-10'>
            <span className='spinner-border text-danger' />
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-3'>
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                  <th className='min-w-200px'>Franquia</th>
                  <th className='text-center min-w-100px'>Unidades</th>
                  <th className='text-end min-w-125px'></th>
                </tr>
              </thead>
              <tbody className='text-gray-700 fw-semibold'>
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={3} className='text-center text-muted py-8'>
                      Nenhuma franquia encontrada
                    </td>
                  </tr>
                )}
                {visible.map((franchise) => (
                  <Fragment key={franchise.id}>
                    <tr className={franchise.units.length ? 'border-bottom-0' : undefined}>
                      <td className={franchise.units.length ? 'text-gray-900 fw-bold' : 'text-muted'}>
                        {franchise.name}
                      </td>
                      <td className='text-center'>
                        <span
                          className={`badge ${franchise.units.length ? 'badge-light-danger' : 'badge-light'}`}
                        >
                          {franchise.units.length}
                        </span>
                      </td>
                      <td className='text-end'>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-danger'
                          onClick={() => addUnit(franchise)}
                        >
                          <KTIcon iconName='plus' className='fs-4' />
                          Maquina
                        </button>
                      </td>
                    </tr>
                    {franchise.units.map((unit, index) => (
                      <tr
                        key={unit.id ?? `new-${index}`}
                        className={index < franchise.units.length - 1 ? 'border-bottom-0' : undefined}
                      >
                        <td colSpan={2} className='pt-0 ps-8'>
                          <div className='d-flex flex-wrap align-items-center gap-4'>
                            <span className='fs-7 fw-bold text-gray-800 min-w-100px'>
                              {unitLabel(unit)}
                            </span>
                            {franchise.rooms.length === 0 ? (
                              <span className='text-warning fs-7'>
                                Nenhuma sala desta franquia possui Depilação a Laser.
                              </span>
                            ) : (
                              franchise.rooms.map((room) => {
                                const owner = roomOwner(franchise, room.id)
                                const takenByOther = owner !== -1 && owner !== index
                                return (
                                  <label
                                    key={room.id}
                                    className='form-check form-check-sm form-check-custom form-check-solid'
                                    title={
                                      takenByOther
                                        ? `Sala já possui a ${unitLabel(franchise.units[owner])}`
                                        : undefined
                                    }
                                  >
                                    <input
                                      className='form-check-input'
                                      type='checkbox'
                                      checked={unit.rooms.includes(room.id)}
                                      disabled={takenByOther}
                                      onChange={() => toggleRoom(franchise, index, room.id)}
                                    />
                                    <span
                                      className={`form-check-label fs-7 ${takenByOther ? 'text-muted' : ''}`}
                                    >
                                      {room.name}
                                    </span>
                                  </label>
                                )
                              })
                            )}
                          </div>
                        </td>
                        <td className='pt-0 text-end'>
                          <button
                            type='button'
                            className='btn btn-sm btn-icon btn-light-danger'
                            title='Remover unidade'
                            onClick={() => removeUnit(franchise, index)}
                          >
                            <KTIcon iconName='trash' className='fs-4' />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className='card-footer d-flex justify-content-end py-6 px-9'>
        <button type='button' className='btn btn-danger' disabled={loading || saving} onClick={save}>
          {!saving && 'Salvar franquias'}
          {saving && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Salvando... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </div>
  )
}
